/**
 * WorksheetPreview - Preview mode for the worksheet
 *
 * Features:
 * - Renders worksheet as it would print
 * - Toggle answer key overlay
 * - Page layout simulation (letter/portrait)
 * - Print-friendly styling
 */

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Eye, Printer, X, FileText, Target, Clock } from "lucide-react";
import { ComponentRenderer } from "./ComponentRenderer";
import type { WorksheetTemplate, WorksheetComponent } from "./types";

// ============================================================================
// Types
// ============================================================================

export interface WorksheetPreviewProps {
  worksheet: WorksheetTemplate;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

// ============================================================================
// Preview Page Component
// ============================================================================

interface PreviewPageProps {
  worksheet: WorksheetTemplate;
  showAnswers: boolean;
}

function PreviewPage({ worksheet, showAnswers }: PreviewPageProps) {
  const { layout, components } = worksheet;

  // Sort components by position (top to bottom, left to right)
  const sortedComponents = useMemo(() => {
    return [...components].sort((a, b) => {
      const yDiff = a.position.y - b.position.y;
      if (Math.abs(yDiff) > 0.05) return yDiff; // Different rows
      return a.position.x - b.position.x; // Same row, sort by x
    });
  }, [components]);

  // Calculate total points
  const totalPoints = useMemo(() => {
    return components.reduce((sum, c) => sum + (c.points || 0), 0);
  }, [components]);

  const autoGradablePoints = useMemo(() => {
    return components
      .filter((c) => c.autoGradable)
      .reduce((sum, c) => sum + (c.points || 0), 0);
  }, [components]);

  return (
    <div
      className={cn(
        "bg-white shadow-lg mx-auto",
        "print:shadow-none print:mx-0",
        layout.orientation === "portrait"
          ? "w-[8.5in] min-h-[11in]"
          : "w-[11in] min-h-[8.5in]"
      )}
      style={{
        padding: `${layout.margins.top}in ${layout.margins.right}in ${layout.margins.bottom}in ${layout.margins.left}in`,
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6 print:mb-4">
        <div>
          <h1 className="text-xl font-bold">{worksheet.title}</h1>
          {worksheet.description && (
            <p className="text-sm text-muted-foreground mt-1">
              {worksheet.description}
            </p>
          )}
          <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
            {worksheet.grade.length > 0 && (
              <span>Grade {worksheet.grade.join(", ")}</span>
            )}
            {worksheet.estimatedMinutes && (
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {worksheet.estimatedMinutes} min
              </span>
            )}
            <span>{totalPoints} points</span>
          </div>
        </div>

        {/* Name field at top right (if exists) */}
        {sortedComponents.find((c) => c.type === "name") && (
          <div className="flex items-baseline gap-2">
            <span className="text-sm">Name:</span>
            <div className="w-48 border-b-2 border-gray-400" />
          </div>
        )}
      </div>

      {/* Answer key indicator */}
      {showAnswers && (
        <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-700 print:bg-blue-100">
          <strong>ANSWER KEY</strong> - Correct answers shown in blue
        </div>
      )}

      <Separator className="mb-6 print:mb-4" />

      {/* Components */}
      <div className="space-y-6 print:space-y-4">
        {sortedComponents
          .filter((c) => c.type !== "name") // Name is in header
          .map((component, index) => (
            <div key={component.id} className="flex gap-4">
              {/* Question number */}
              <div className="shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center text-sm font-medium">
                {index + 1}
              </div>

              {/* Component content */}
              <div className="flex-1">
                <ComponentRenderer
                  component={component}
                  showAnswer={showAnswers}
                />

                {/* Points indicator (print mode) */}
                {component.points !== undefined && component.points > 0 && (
                  <div className="text-xs text-muted-foreground mt-1 print:text-gray-500">
                    ({component.points} pt{component.points !== 1 ? "s" : ""})
                  </div>
                )}
              </div>
            </div>
          ))}
      </div>

      {/* Footer with curriculum info */}
      {worksheet.outcomeIds.length > 0 && (
        <div className="mt-auto pt-6 print:pt-4">
          <Separator className="mb-4 print:mb-2" />
          <div className="flex items-center gap-2 text-xs text-muted-foreground print:text-gray-500">
            <Target className="h-3 w-3" />
            <span>Outcomes: {worksheet.outcomeIds.join(", ")}</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================================================
// Main Preview Dialog
// ============================================================================

export function WorksheetPreview({
  worksheet,
  open,
  onOpenChange,
}: WorksheetPreviewProps) {
  const [showAnswers, setShowAnswers] = useState(false);

  const handlePrint = () => {
    window.print();
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] w-[1000px] h-[90vh] flex flex-col p-0">
        <DialogHeader className="px-6 py-4 border-b shrink-0">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="flex items-center gap-2">
                <Eye className="h-5 w-5" />
                Preview
              </DialogTitle>
              <DialogDescription>
                Preview how your worksheet will look when printed
              </DialogDescription>
            </div>

            <div className="flex items-center gap-4">
              {/* Answer key toggle */}
              <div className="flex items-center gap-2">
                <Switch
                  id="showAnswers"
                  checked={showAnswers}
                  onCheckedChange={setShowAnswers}
                />
                <Label
                  htmlFor="showAnswers"
                  className="text-sm cursor-pointer"
                >
                  Show Answer Key
                </Label>
              </div>

              {/* Print button */}
              <Button onClick={handlePrint} className="gap-2">
                <Printer className="h-4 w-4" />
                Print
              </Button>
            </div>
          </div>
        </DialogHeader>

        {/* Preview content */}
        <ScrollArea className="flex-1">
          <div className="p-6 bg-gray-100 min-h-full">
            {worksheet.components.length > 0 ? (
              <PreviewPage worksheet={worksheet} showAnswers={showAnswers} />
            ) : (
              <div className="flex items-center justify-center h-full text-muted-foreground">
                <div className="text-center">
                  <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
                  <p>No components to preview</p>
                  <p className="text-sm mt-1">
                    Add components to the canvas first
                  </p>
                </div>
              </div>
            )}
          </div>
        </ScrollArea>

        {/* Footer with stats */}
        <div className="px-6 py-3 border-t bg-muted/30 flex items-center justify-between text-sm">
          <div className="flex items-center gap-4 text-muted-foreground">
            <span>{worksheet.components.length} components</span>
            <span>
              {worksheet.gradingConfig?.totalPoints || 0} total points
            </span>
            <span>
              {worksheet.gradingConfig?.autoGradablePoints || 0} auto-gradable
            </span>
          </div>

          <div className="flex items-center gap-2">
            {worksheet.status === "draft" && (
              <Badge variant="secondary">Draft</Badge>
            )}
            {worksheet.status === "published" && (
              <Badge variant="default">Published</Badge>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
